import React, { useRef, useState } from "react";
import { container } from "@/utils/mobx";
import { TabsStore, Tab } from "./store";
import { Clickable } from "@/components/toolkit/Clickable";
import { ContextMenu } from "@/components/toolkit/ContextMenu";
import { t } from "@/i18n";
import "./s.tabs.css";

const LONG_PRESS_MS = 500;

export const Tabs = container((props: {
    store: TabsStore;
    tabs: Tab[];
    activeTabPath: string | null;
    onTabChange?: (path: string) => any;
    onTabClose?: (path: string) => any;
    onTabCloseAll?: () => any;
    onTabCloseOthers?: (path: string) => any;
    onTabReorder?: (fromIndex: number, toIndex: number) => any;
    leftSlot?: React.ReactNode;
    children?: React.ReactNode;
}) => {
    const store = props.store;
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressMoved = useRef(false);
    const barRef = useRef<HTMLDivElement>(null);

    const dragFromIndex = useRef<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const handleWheel = (e: React.WheelEvent) => {
        if (e.deltaY !== 0 && barRef.current) {
            barRef.current.scrollLeft += e.deltaY;
        }
    };

    const showContextMenu = (path: string, x: number, y: number) => {
        store.contextMenuStore.show(x, y, [
            { title: t("tabs_close_tab"), value: "close_tab" },
            { title: t("tabs_close_other_tabs"), value: "close_other_tabs" },
            { title: t("tabs_close_all_tabs"), value: "close_all_tabs" },
        ], (value: string) => {
            if (value === "close_tab") store.closeTab(path);
            else if (value === "close_other_tabs") store.onTabCloseOthers(path);
            else if (value === "close_all_tabs") store.onTabCloseAll();
        });
    };

    const handleTouchStart = (path: string, e: React.TouchEvent) => {
        longPressMoved.current = false;
        const touch = e.touches[0];
        longPressTimer.current = setTimeout(() => {
            if (!longPressMoved.current) {
                showContextMenu(path, touch.clientX, touch.clientY);
            }
        }, LONG_PRESS_MS);
    };

    const handleTouchMove = () => {
        longPressMoved.current = true;
        if (longPressTimer.current !== null) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current !== null) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const handleDragStart = (index: number, e: React.DragEvent) => {
        dragFromIndex.current = index;
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (index: number, e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragOverIndex !== index) setDragOverIndex(index);
    };

    const handleDrop = (index: number, e: React.DragEvent) => {
        e.preventDefault();
        if (dragFromIndex.current !== null && dragFromIndex.current !== index) {
            store.onTabReorder(dragFromIndex.current, index);
        }
        dragFromIndex.current = null;
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        dragFromIndex.current = null;
        setDragOverIndex(null);
    };

    const handleBarDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!barRef.current?.contains(e.relatedTarget as Node)) {
            setDragOverIndex(null);
        }
    };

    return (
        <div className="tabs">
            <div className="tabs__bar">
                {props.leftSlot}
                <div className="tabs__tab_list" ref={barRef} onWheel={handleWheel} onDragLeave={handleBarDragLeave}>
                {store.tabs.map((tab, i) => (
                    <Clickable key={tab.path} onClick={() => store.selectTab(tab.path)}>
                        <div
                            className={
                                "tabs__tab" +
                                (store.activeTabPath === tab.path ? " active" : "") +
                                (dragOverIndex === i && dragFromIndex.current !== i ? " drag-over" : "")
                            }
                            draggable
                            onDragStart={(e) => handleDragStart(i, e)}
                            onDragOver={(e) => handleDragOver(i, e)}
                            onDrop={(e) => handleDrop(i, e)}
                            onDragEnd={handleDragEnd}
                            onContextMenu={(e) => { e.preventDefault(); showContextMenu(tab.path, e.clientX, e.clientY); }}
                            onTouchStart={(e) => handleTouchStart(tab.path, e)}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            <span className="tabs__tab_label">{tab.label}</span>
                            <Clickable stopPropagation onClick={() => store.closeTab(tab.path)}>
                                <span className="tabs__tab_close">×</span>
                            </Clickable>
                        </div>
                    </Clickable>
                ))}
                </div>
            </div>
            <div className="tabs__content">
                {props.children}
            </div>
            <ContextMenu store={store.contextMenuStore} options={[]} />
        </div>
    );
}, TabsStore);
